import React, { useRef, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import './AccountDetailsDialog.css';

interface AccountDetail {
  acctrefno: number;
  loan_number: string;
  customer_name: string;
  email_address: string;
  current_principal_balance: number;
  current_interest_balance: number;
  current_payoff_balance: number;
  days_past_due: number;
  last_extension_date: string;
  deferment_count: number;
  last_promise_date: string;
  last_promise_amount: string;
  last_payment_date: string;
  last_payment_amount: string;
  account_manager: string;
  // cease_and_desist: string;
  loan_group: string;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface SortingInfo {
  sort_by: string;
  sort_order: string;
}

interface AccountDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: AccountDetail[];
  title: string;
  loading?: boolean;
  onLoanNumberClick?: (loanNumber: string) => void;
  pagination?: PaginationInfo;
  sorting?: SortingInfo;
  onPageChange?: (page: number) => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  onSearchChange?: (searchLoanNumber: string, searchCustomerName: string) => void;
  searchLoanNumber?: string;
  searchCustomerName?: string;
  isSearchPending?: boolean;
  onSearchComplete?: () => void;
  onExportAllData?: () => Promise<AccountDetail[]>;
  totals?: {total_principal_balance: number, total_payoff_balance: number, total_balance: number};
}

const AccountDetailsDialog: React.FC<AccountDetailsDialogProps> = ({
  isOpen,
  onClose,
  accounts,
  title,
  loading = false,
  onLoanNumberClick,
  pagination,
  sorting,
  onPageChange,
  onSortChange,
  onSearchChange,
  searchLoanNumber = '',
  searchCustomerName = '',
  isSearchPending = false,
  onSearchComplete,
  onExportAllData,
  totals
}) => {
  // Refs for input fields to maintain focus
  const loanNumberInputRef = useRef<HTMLInputElement>(null);
  const customerNameInputRef = useRef<HTMLInputElement>(null);
  const lastFocusedInputRef = useRef<'loan' | 'customer' | null>(null);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const exportAbortControllerRef = useRef<AbortController | null>(null);

  // Restore focus after search completes
  useEffect(() => {
    if (!isSearchPending && lastFocusedInputRef.current && onSearchComplete) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        if (lastFocusedInputRef.current === 'loan' && loanNumberInputRef.current) {
          loanNumberInputRef.current.focus();
          // Position cursor at end of text
          const length = loanNumberInputRef.current.value.length;
          loanNumberInputRef.current.setSelectionRange(length, length);
        } else if (lastFocusedInputRef.current === 'customer' && customerNameInputRef.current) {
          customerNameInputRef.current.focus();
          // Position cursor at end of text
          const length = customerNameInputRef.current.value.length;
          customerNameInputRef.current.setSelectionRange(length, length);
        }
        onSearchComplete();
      }, 50);
    }
  }, [isSearchPending, onSearchComplete]);

  // Cleanup export when dialog closes
  useEffect(() => {
    return () => {
      // Cancel any ongoing export when component unmounts or dialog closes
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
        console.log('Export cancelled due to dialog close');
      }
    };
  }, []);

  if (!isOpen) return null;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === 'N/A') return dateString;
    
    // Handle YYYY/mm/dd format
    if (dateString.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
      const [year, month, day] = dateString.split('/');
      return `${month}/${day}/${year}`;
    }
    
    // Return as-is if already in other format
    return dateString;
  };

  // Use totals from backend if available (for all accounts), otherwise calculate from current page
  // totalBalance should match what's shown on the card (principal + interest)
  const totalBalance = totals?.total_balance ?? totals?.total_payoff_balance ?? (Array.isArray(accounts) ? accounts.reduce((sum, account) => sum + ((account.current_principal_balance || 0) + (account.current_interest_balance || 0)), 0) : 0);
  const totalPrincipalBalance = totals?.total_principal_balance ?? (Array.isArray(accounts) ? accounts.reduce((sum, account) => sum + (account.current_principal_balance || 0), 0) : 0);
  const totalPayoffBalance = totals?.total_payoff_balance ?? (Array.isArray(accounts) ? accounts.reduce((sum, account) => sum + (account.current_payoff_balance || 0), 0) : 0);
  
  // Use pagination total count if available, otherwise use current accounts length
  const totalAccountsCount = pagination?.total_count || (Array.isArray(accounts) ? accounts.length : 0);

  const handleSort = (column: string) => {
    if (!onSortChange) return;
    
    const currentSortBy = sorting?.sort_by || 'days_past_due';
    const currentSortOrder = sorting?.sort_order || 'DESC';
    
    // Toggle sort order if clicking the same column, otherwise default to DESC
    const newSortOrder = currentSortBy === column && currentSortOrder === 'DESC' ? 'ASC' : 'DESC';
    onSortChange(column, newSortOrder);
  };

  const getSortIcon = (column: string) => {
    if (!sorting || sorting.sort_by !== column) return '↕️';
    return sorting.sort_order === 'ASC' ? '↑' : '↓';
  };

  const exportToExcel = async () => {
    console.log('Export button clicked');
    
    if (!onExportAllData) {
      console.error('Export functionality not available - onExportAllData is null');
      alert('Export functionality not available');
      return;
    }

    if (isExporting) {
      console.log('Export already in progress, ignoring click');
      return;
    }

    try {
      console.log('Starting export process...');
      setIsExporting(true);
      setExportProgress('Preparing export...');

      // Create abort controller for this export
      exportAbortControllerRef.current = new AbortController();

      console.log('Fetching all data for export...');
      setExportProgress('Fetching data...');
      // Fetch all data for export (ignoring pagination)
      const allAccounts = await onExportAllData();
      
      console.log('Received accounts for export:', allAccounts.length);
      
      if (allAccounts.length === 0) {
        console.log('No data to export');
        alert('No data to export');
        return;
      }

      setExportProgress('Processing data...');
      // Prepare data for Excel export
      const exportData = allAccounts.map(account => ({
      'Loan Number': account.loan_number,
      'Customer Name': account.customer_name,
      'Email Address': account.email_address,
      'Days Past Due': account.days_past_due,
      'Principal Balance': account.current_principal_balance,
      'Interest Balance': account.current_interest_balance,
      'Payoff Balance': account.current_payoff_balance,
      'Last Extension Date': account.last_extension_date,
      'Deferment Count': account.deferment_count,
      'Last Promise Date': account.last_promise_date,
      'Last Promise Amount': account.last_promise_amount,
      'Last Payment Date': account.last_payment_date,
      'Last Payment Amount': account.last_payment_amount,
      'Account Manager': account.account_manager,
      // 'Cease and Desist': account.cease_and_desist,
      'Loan Group': account.loan_group
    }));

    // Calculate totals for all accounts - using payoff balance to match backend calculation (principal + interest)
    const totalBalance = allAccounts.reduce((sum, account) => {
      // Use payoff balance (principal + interest) to match backend totalBalance calculation
      return sum + (account.current_payoff_balance || (account.current_principal_balance || 0) + (account.current_interest_balance || 0));
    }, 0);
    const totalPayoffBalance = allAccounts.reduce((sum, account) => sum + (account.current_payoff_balance || 0), 0);

    // Add summary row
    const summaryRow = {
      'Loan Number': 'TOTAL',
      'Customer Name': '',
      'Email Address': '',
      'Days Past Due': '',
      'Principal Balance': totalBalance,
      'Interest Balance': '',
      'Payoff Balance': totalPayoffBalance,
      'Last Extension Date': '',
      'Deferment Count': '',
      'Last Promise Date': '',
      'Last Promise Amount': '',
      'Last Payment Date': '',
      'Last Payment Amount': '',
      'Account Manager': ''
    };

    setExportProgress('Creating Excel file...');
    // Create worksheet
    const ws = XLSX.utils.json_to_sheet([...exportData, summaryRow]);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Loan Number
      { wch: 20 }, // Customer Name
      { wch: 25 }, // Email Address
      { wch: 12 }, // Days Past Due
      { wch: 18 }, // Principal Balance
      { wch: 18 }, // Interest Balance
      { wch: 18 }, // Payoff Balance
      { wch: 15 }, // Last Extension Date
      { wch: 12 }, // Deferment Count
      { wch: 20 }, // Last Promise
      { wch: 20 }, // Last Payment
      { wch: 15 }, // Account Manager
      { wch: 15 }  // Loan Group
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Account Details');

    setExportProgress('Generating file...');
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `Account_Details_${timestamp}.xlsx`;

    // Export file
    XLSX.writeFile(wb, filename);
    
    setExportProgress(`Export completed! ${allAccounts.length} records exported.`);
    
    } catch (error) {
      console.error('Export failed:', error);
      
      // Check if export was cancelled
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Export was cancelled');
        setExportProgress('Export cancelled');
        return; // Don't show error alert for cancelled exports
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const errorStack = error instanceof Error ? error.stack : undefined;
      const errorName = error instanceof Error ? error.name : 'Unknown';
      
      console.error('Error details:', {
        message: errorMessage,
        stack: errorStack,
        name: errorName
      });
      setExportProgress(`Export failed: ${errorMessage}. Please try again.`);
    } finally {
      // Reset export state
      setIsExporting(false);
      exportAbortControllerRef.current = null;
      
      // Keep the progress message visible for a few seconds, then clear it
      setTimeout(() => {
        setExportProgress('');
      }, 3000);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{title}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="dialog-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading account details...</p>
            </div>
          ) : (
            <>
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Accounts:</span>
                  <span className="stat-value">{totalAccountsCount.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Showing:</span>
                  <span className="stat-value">
                    {pagination ? 
                      `${((pagination.page - 1) * pagination.page_size + 1).toLocaleString()}-${Math.min(pagination.page * pagination.page_size, pagination.total_count).toLocaleString()} of ${pagination.total_count.toLocaleString()}` :
                      `${accounts.length.toLocaleString()} of ${totalAccountsCount.toLocaleString()}`
                    }
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Balance:</span>
                  <span className="stat-value">{formatCurrency(totalBalance)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Principal:</span>
                  <span className="stat-value">{formatCurrency(totalPrincipalBalance)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Payoff:</span>
                  <span className="stat-value">{formatCurrency(totalPayoffBalance)}</span>
                </div>
              </div>

              {/* Search Controls */}
              {onSearchChange && (
                <div className="search-controls">
                  <div className="search-input-group">
                    <label htmlFor="search-loan-number">Search by Loan Number:</label>
                    <input
                      ref={loanNumberInputRef}
                      id="search-loan-number"
                      type="text"
                      placeholder="Enter loan number..."
                      value={searchLoanNumber}
                      onChange={(e) => onSearchChange(e.target.value, searchCustomerName)}
                      onFocus={() => lastFocusedInputRef.current = 'loan'}
                      className="search-input"
                    />
                  </div>
                  <div className="search-input-group">
                    <label htmlFor="search-customer-name">Search by Customer Name:</label>
                    <input
                      ref={customerNameInputRef}
                      id="search-customer-name"
                      type="text"
                      placeholder="Enter customer name..."
                      value={searchCustomerName}
                      onChange={(e) => onSearchChange(searchLoanNumber, e.target.value)}
                      onFocus={() => lastFocusedInputRef.current = 'customer'}
                      className="search-input"
                    />
                  </div>
                  <div className="search-actions">
                    {isSearchPending && (
                      <div className="search-pending-indicator">
                        <span className="search-pending-text">Searching...</span>
                      </div>
                    )}
                    <button 
                      className="clear-search-button"
                      onClick={() => onSearchChange('', '')}
                      disabled={!searchLoanNumber && !searchCustomerName}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              )}

              <div className="table-container">
                <table className="accounts-table">
                  <thead>
                    <tr>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('loan_number')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Loan Number {onSortChange && getSortIcon('loan_number')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('customer_name')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Customer Name {onSortChange && getSortIcon('customer_name')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('email_address')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Email Address {onSortChange && getSortIcon('email_address')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('days_past_due')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Days Past Due {onSortChange && getSortIcon('days_past_due')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('current_principal_balance')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Principal Balance {onSortChange && getSortIcon('current_principal_balance')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('current_interest_balance')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Interest Balance {onSortChange && getSortIcon('current_interest_balance')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('current_payoff_balance')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Payoff Balance {onSortChange && getSortIcon('current_payoff_balance')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('last_extension_date')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Last Extension {onSortChange && getSortIcon('last_extension_date')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('deferment_count')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Deferments {onSortChange && getSortIcon('deferment_count')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('last_promise_date')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Last Promise {onSortChange && getSortIcon('last_promise_date')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('last_payment_date')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Last Payment {onSortChange && getSortIcon('last_payment_date')}
                      </th>
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('account_manager')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Account Manager {onSortChange && getSortIcon('account_manager')}
                      </th>
                      {/* <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('cease_and_desist')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Cease and Desist {onSortChange && getSortIcon('cease_and_desist')}
                      </th> */}
                      <th 
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('loan_group')}
                        title={onSortChange ? 'Click to sort' : ''}
                      >
                        Loan Group {onSortChange && getSortIcon('loan_group')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(accounts) || accounts.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="no-data">
                          No accounts found
                        </td>
                      </tr>
                    ) : (
                      Array.isArray(accounts) ? accounts.map((account, index) => (
                        <tr key={account.loan_number} className={index % 2 === 0 ? 'even' : 'odd'}>
                          <td className="loan-number">
                            {onLoanNumberClick ? (
                              <button 
                                className="loan-number-link"
                                onClick={() => onLoanNumberClick(account.loan_number)}
                                title="Click to view loan details"
                              >
                                {account.loan_number}
                              </button>
                            ) : (
                              account.loan_number
                            )}
                          </td>
                          <td className="customer-name">{account.customer_name || 'N/A'}</td>
                          <td className="email-address">{account.email_address || 'N/A'}</td>
                          <td className="days-past-due">
                            <span className={`days-badge ${
                              account.days_past_due === 0 ? 'current' : 
                              account.days_past_due < 10 ? 'slightly-past-due' : 
                              account.days_past_due < 30 ? 'moderately-past-due' : 
                              'severely-past-due'
                            }`}>
                              {account.days_past_due}
                            </span>
                          </td>
                          <td className="balance principal">{formatCurrency(account.current_principal_balance || 0)}</td>
                          <td className="balance interest">{formatCurrency(account.current_interest_balance || 0)}</td>
                          <td className="balance payoff">{formatCurrency(account.current_payoff_balance || 0)}</td>
                          <td className="last-extension-date">{account.last_extension_date || 'N/A'}</td>
                          <td className="deferment-count">{account.deferment_count}</td>
                          <td className="last-promise">
                            {account.last_promise_date ? `${account.last_promise_date} (${account.last_promise_amount})` : 'N/A'}
                          </td>
                          <td className="last-payment">
                            {account.last_payment_date ? `${account.last_payment_date} (${account.last_payment_amount})` : 'N/A'}
                          </td>
                          <td className="account-manager">{account.account_manager || 'N/A'}</td>
                          {/* <td className="cease-desist">{formatDate(account.cease_and_desist || 'N/A')}</td> */}
                          <td className="loan-group">{account.loan_group || 'N/A'}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={14} className="no-data">
                            Invalid data format
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pagination && onPageChange && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Page {pagination.page} of {pagination.total_pages} 
                      ({pagination?.total_count?.toLocaleString() || '0'} total accounts)
                    </span>
                  </div>
                  
                  <div className="pagination-buttons">
                    <button 
                      className="pagination-button"
                      onClick={() => onPageChange(pagination.page - 1)}
                      disabled={!pagination.has_previous || loading}
                      title="Previous page"
                    >
                      ← Previous
                    </button>
                    
                    <div className="page-numbers">
                      {(() => {
                        const pages = [];
                        const startPage = Math.max(1, pagination.page - 2);
                        const endPage = Math.min(pagination.total_pages, pagination.page + 2);
                        
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              className={`page-number ${i === pagination.page ? 'active' : ''}`}
                              onClick={() => onPageChange(i)}
                              disabled={loading}
                            >
                              {i}
                            </button>
                          );
                        }
                        return pages;
                      })()}
                    </div>
                    
                    <button 
                      className="pagination-button"
                      onClick={() => onPageChange(pagination.page + 1)}
                      disabled={!pagination.has_next || loading}
                      title="Next page"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="dialog-footer">
          <div className="export-section">
            <button 
              className="export-excel-button" 
              onClick={exportToExcel}
              disabled={loading || isExporting || !Array.isArray(accounts) || accounts.length === 0}
              title={isExporting ? "Export in progress..." : "Export to Excel"}
            >
              {isExporting ? '⏳ Exporting...' : '📊 Export to Excel'}
            </button>
            {exportProgress && (
              <div className={`export-progress ${
                exportProgress.includes('Export completed!') ? 'success' : 
                exportProgress.includes('Export failed') ? 'error' : ''
              }`}>
                {isExporting && <div className="export-spinner"></div>}
                {!isExporting && exportProgress.includes('Export completed!') && <span>✅</span>}
                {!isExporting && exportProgress.includes('Export failed') && <span>❌</span>}
                <span className="export-progress-text">{exportProgress}</span>
              </div>
            )}
          </div>
          <button className="close-dialog-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountDetailsDialog;
